import React, { useState, useEffect } from "react";
import { Box, Typography, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Select, MenuItem, IconButton, CircularProgress } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import { backendUrl, API_KEYS } from '../config';

export default function AssociationSettingsPage({ currentUser, groupId }) {
  const { t } = useTranslation();
  const [associations, setAssociations] = useState([]);
  const [apps, setApps] = useState([]); // Liste dynamique des applications
  const [appsLoading, setAppsLoading] = useState(true);
  const [newExt, setNewExt] = useState("");
  const [newApp, setNewApp] = useState("");
  const [saveStatus, setSaveStatus] = useState(null); // null | 'success' | 'error' | 'forbidden'
  const [saving, setSaving] = useState(false);
  const [editExt, setEditExt] = useState(null); // extension en édition
  const [editApp, setEditApp] = useState("");

  useEffect(() => {
    setAppsLoading(true);
    fetch(`${backendUrl}/applications/`, {
      headers: { 'x-api-key': API_KEYS.key }
    })
      .then(res => res.json())
      .then(data => {
        setApps(Array.isArray(data) ? data : []);
        setNewApp((Array.isArray(data) && data.length > 0) ? (data[0].key || data[0].id || data[0].name) : "");
        setAppsLoading(false);
      })
      .catch(() => setAppsLoading(false));
  }, []);

  // Fonction utilitaire pour recharger les associations depuis le backend
  const reloadAssociations = () => {
    fetch(`${backendUrl}/api/config/associations`, {
      headers: {
        'x-api-key': API_KEYS.key,
        'x-api-secret': API_KEYS.secret,
        'accept': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data && Array.isArray(data.associations)) {
          setAssociations(data.associations);
        }
      });
  };

  useEffect(() => {
    reloadAssociations();
  }, [currentUser]);

  // Charger les associations existantes depuis le backend
  useEffect(() => {
    reloadAssociations();
  }, []);

  const handleAdd = async () => {
    // Toujours enlever le point initial
    const extValue = String(newExt).replace(/^\./, '').trim().toLowerCase();
    if (!extValue || !newApp) return;
    setSaving(true);
    // Toujours recharger la liste avant d'ajouter
    let latestAssociations = [];
    try {
      const res = await fetch((process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '') + '/api/config/associations', {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY,
          'x-api-secret': process.env.REACT_APP_API_SECRET,
          'accept': 'application/json'
        }
      });
      const data = await res.json();
      latestAssociations = Array.isArray(data.associations) ? data.associations : [];
    } catch (e) {
      setSaveStatus('error');
      setSaving(false);
      return;
    }
    // Ne pas ajouter de doublon
    if (latestAssociations.some(a => a.ext === newExt)) {
      setSaveStatus('error');
      setSaving(false);
      return;
    }
    // On force la structure attendue par le backend
    const payload = {
      associations: [...latestAssociations, { ext: extValue, app: String(newApp) }].map(a => ({ ext: String(a.ext).replace(/^\./, '').trim().toLowerCase(), app: a.app }))
    };
    //console.log('[DEBUG] Payload envoyé au backend:', payload);
    fetch(`${backendUrl}/api/config/associations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEYS.key,
        'x-api-secret': API_KEYS.secret,
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(async res => {
        if (res.ok) {
          setSaveStatus('success');
          reloadAssociations();
        } else {
          const errorText = await res.text();
          //console.error('[DEBUG] Erreur backend:', errorText);
          if (res.status === 401 || res.status === 403) {
            setSaveStatus('forbidden');
          } else {
            setSaveStatus('error');
          }
        }
      })
      .catch(err => {
        setSaveStatus('error');
        //console.error('[DEBUG] Exception lors du POST:', err);
      })
      .finally(() => {
        setSaving(false);
        setNewExt("");
        setNewApp(apps.length > 0 ? String(apps[0].id) : "");
      });
  };

  const handleDelete = (ext) => {
    setSaving(true);
    const extValue = String(ext).replace(/^\./, '').trim().toLowerCase();
    fetch(`${backendUrl}/api/config/associations/${encodeURIComponent(extValue)}`, {
      method: 'DELETE',
      headers: {
        'x-api-key': API_KEYS.key,
        'x-api-secret': API_KEYS.secret,
        'accept': 'application/json'
      }
    })
      .then(res => {
        if (res.ok) {
          setSaveStatus('success');
          reloadAssociations();
        } else if (res.status === 401 || res.status === 403) {
          setSaveStatus('forbidden');
        } else {
          setSaveStatus('error');
        }
      })
      .catch(() => setSaveStatus('error'))
      .finally(() => setSaving(false));
  };

  const handleEdit = (ext, app) => {
    setEditExt(ext);
    setEditApp(app);
  };

  const handleEditSave = (ext) => {
    if (!editApp) {
      setSaveStatus('error');
      alert('Aucune application sélectionnée pour cette extension.');
      return;
    }
    setSaving(true);
    const extValue = String(ext).replace(/^\./, '').trim().toLowerCase();
    const payload = { ext: extValue, app: editApp };
    //console.log('[DEBUG] PUT payload', payload);
    fetch(`${backendUrl}/api/config/associations/${encodeURIComponent(extValue)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEYS.key,
        'x-api-secret': API_KEYS.secret,
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (res.ok) {
          setSaveStatus('success');
          reloadAssociations();
        } else if (res.status === 401 || res.status === 403) {
          setSaveStatus('forbidden');
        } else {
          setSaveStatus('error');
        }
      })
      .catch(() => setSaveStatus('error'))
      .finally(() => {
        setSaving(false);
        setEditExt(null);
        setEditApp("");
      });
  };



  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const res = await fetch((process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '') + '/api/config/associations', {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY,
          'x-api-secret': process.env.REACT_APP_API_SECRET,
          'accept': 'application/json'
        }
      });
      const data = await res.json();
      const latestAssociations = Array.isArray(data.associations) ? data.associations : [];
      const payload = {
        associations: [...latestAssociations, ...associations.filter(a => !latestAssociations.some(b => b.ext === a.ext))].map(a => ({ ext: a.ext, app: a.app }))
      };
      //console.log('[DEBUG] Payload envoyé au backend:', payload);
      const res2 = await fetch((process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '') + '/api/config/associations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY,
          'x-api-secret': process.env.REACT_APP_API_SECRET,
          'accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (res2.ok) {
        setSaveStatus('success');
        reloadAssociations();
      } else if (res2.status === 401 || res2.status === 403) {
        setSaveStatus('forbidden');
      } else {
        setSaveStatus('error');
      }
    } catch (e) {
      setSaveStatus('error');
    }
    setSaving(false);
  };

  return (
    <Box sx={{ p: 4, maxWidth: 600, mx: "auto" }}>
      <Box display="flex" alignItems="center" mb={2}>
        <SaveIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5">{t('Association.extensions.applications')}</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" mb={3}>
        {t('Association.extensions.applications.description')}
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('Association.extensions.applications.table.extension')}</TableCell>
              <TableCell>{t('Association.extensions.applications.table.application')}</TableCell>
              <TableCell align="right">{t('Association.extensions.applications.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {associations.map((row) => (
              <TableRow key={row.ext}>
                <TableCell>{row.ext}</TableCell>
                <TableCell>
                  {editExt === row.ext ? (
                    <Select
                      size="small"
                      value={editApp}
                      onChange={e => setEditApp(e.target.value)}    
                      sx={{ minWidth: 150 }}
                    >
                      {apps.map(app => (
                        <MenuItem key={app.id} value={String(app.id)}>
                          {app.label || app.name || app.key || app.id}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    (() => {
                      const found = apps.find(a => String(a.key ?? a.id ?? a.name) === String(row.app));
                      return found ? (found.label || found.name || found.key || found.id) : row.app;
                    })()
                  )}
                </TableCell>
                <TableCell align="right">
                  {editExt === row.ext ? (
                    <>
                      <IconButton color="primary" onClick={() => handleEditSave(row.ext)} size="small" disabled={saving}>
                        <CheckIcon />
                      </IconButton>
                      <IconButton color="secondary" onClick={() => setEditExt(null)} size="small">
                        <CloseIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton color="primary" onClick={() => handleEdit(row.ext, row.app)} size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(row.ext)} size="small" disabled={saving}>
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <TextField
                  size="small"
                  value={newExt}
                  onChange={e => {
                    // Toujours préfixer d'un point à l'affichage, mais stocker sans point
                    let val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
                    setNewExt(val ? '.' + val : '');
                  }}
                  placeholder=".ext"
                  sx={{ minWidth: 80 }}
                  disabled={saving}
                />
              </TableCell>
              <TableCell>
                {appsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Select
                    size="small"
                    value={newApp}
                    onChange={e => setNewApp(e.target.value)}
                    sx={{ minWidth: 150 }}
                    disabled={saving}
                  >
                    {apps.map(app => (
                      <MenuItem key={app.id} value={String(app.id)}>
                        {app.label || app.name || app.key || app.id}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </TableCell>
              <TableCell align="right">
                <IconButton color="primary" onClick={handleAdd} size="small" disabled={!newExt || !newApp || saving}>
                  <AddIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <IconButton
          color="primary"
          onClick={handleSave}
          disabled={saving}
          sx={{ mr: 2 }}
          onTouchStart={e => {
            e.stopPropagation();
            handleSave();
          }}
        >
          <SaveIcon />
        </IconButton>
        <Typography variant="button" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {t('Association.extensions.applications.save')}
        </Typography>
      </Box>
      {saveStatus === 'success' && (
        <Alert severity="success" sx={{ mt: 2 }}>{t('Association.extensions.applications.saved')}</Alert>
      )}
      {saveStatus === 'forbidden' && (
        <Alert severity="error" sx={{ mt: 2 }}>{t('Association.extensions.applications.forbidden')}</Alert>
      )}
      {saveStatus === 'error' && (
        <Alert severity="error" sx={{ mt: 2 }}>{t('Association.extensions.applications.error')}</Alert>
      )}
      <Alert severity="info" sx={{ mt: 3 }}>
        {t('Association.extensions.applications.info')}
      </Alert>
    </Box>
  );
}
